import { FormEvent, useEffect, useState } from 'react';

import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { FormField } from '../../components/FormField';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { CloseIcon } from '../../icons/CloseIcon';
import {
  CloseButton,
  FormBlock,
  FormError,
  ForgotRow,
  Heading,
  HeadingAccent,
  HeadingBlock,
  InlineLink,
  Panel,
  SignUpRow,
} from './SignInModal.styles';
import type { SignInModalLabels, SignInModalProps } from './SignInModal.types';

const DEFAULT_LABELS: Required<SignInModalLabels> = {
  title: 'Sign in.',
  subtitle: 'Good things come to those who log in.',
  signUpPrompt: "Don't have an account?",
  signUpLink: 'Sign up',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  submit: 'Sign in',
  forgottenPassword: 'Forgotten password?',
  close: 'Close',

  signUpTitle: 'Sign up.',
  signUpSubtitle: 'Fast now. Faster next time.',
  signInPrompt: 'Already have an account?',
  signInLink: 'Sign in',
  isSpaMember: 'Already a spa member?',
  spaMemberNumberLabel: 'Spa Member No',
  signUpSubmit: 'Sign up',
};

export function SignInModal({
  open,
  onClose,
  values,
  onChange,
  onSubmit,
  isSubmitting = false,
  errors,
  signUpValues,
  onSignUpChange,
  onSignUpSubmit,
  isSigningUp = false,
  signUpErrors,
  onForgottenPasswordClick,
  labels,
}: SignInModalProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');

  useEffect(() => {
    if (!open) setMode('signIn');
  }, [open]);

  const isSignUp = mode === 'signUp' && Boolean(signUpValues && onSignUpChange && onSignUpSubmit);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSignUp) {
      if (isSigningUp) return;
      onSignUpSubmit?.();
      return;
    }
    if (isSubmitting) return;
    onSubmit();
  };

  const ariaLabel = isSignUp ? merged.signUpTitle : merged.title;

  return (
    <Modal open={open} onClose={onClose} ariaLabel={ariaLabel}>
      <Panel data-testid="sign-in-modal">
        <CloseButton type="button" onClick={onClose} aria-label={merged.close}>
          <CloseIcon />
        </CloseButton>

        <HeadingBlock>
          {isSignUp ? (
            <>
              <Heading>
                {merged.signUpTitle} <HeadingAccent>{merged.signUpSubtitle}</HeadingAccent>
              </Heading>
              <SignUpRow>
                {merged.signInPrompt}{' '}
                <InlineLink type="button" onClick={() => setMode('signIn')}>
                  {merged.signInLink}
                </InlineLink>
              </SignUpRow>
            </>
          ) : (
            <>
              <Heading>
                {merged.title} <HeadingAccent>{merged.subtitle}</HeadingAccent>
              </Heading>
              <SignUpRow>
                {merged.signUpPrompt}{' '}
                <InlineLink type="button" onClick={() => setMode('signUp')}>
                  {merged.signUpLink}
                </InlineLink>
              </SignUpRow>
            </>
          )}
        </HeadingBlock>

        <FormBlock onSubmit={handleSubmit} noValidate>
          {isSignUp && signUpValues && onSignUpChange ? (
            <>
              <FormField label={merged.emailLabel} required error={signUpErrors?.email}>
                <Input
                  type="email"
                  autoComplete="email"
                  value={signUpValues.email}
                  onChange={(e) => onSignUpChange('email', e.target.value)}
                  error={Boolean(signUpErrors?.email)}
                  aria-label={merged.emailLabel}
                />
              </FormField>

              <Checkbox
                checked={signUpValues.isSpaMember}
                onChange={(checked) => onSignUpChange('isSpaMember', checked)}
                label={merged.isSpaMember}
              />

              {signUpValues.isSpaMember && (
                <FormField
                  label={merged.spaMemberNumberLabel}
                  required
                  error={signUpErrors?.spaMemberNumber}
                >
                  <Input
                    type="text"
                    value={signUpValues.spaMemberNumber}
                    onChange={(e) => onSignUpChange('spaMemberNumber', e.target.value)}
                    error={Boolean(signUpErrors?.spaMemberNumber)}
                    aria-label={merged.spaMemberNumberLabel}
                  />
                </FormField>
              )}

              {signUpErrors?.form && <FormError role="alert">{signUpErrors.form}</FormError>}

              <Button type="submit" variant="primary" fullWidth disabled={isSigningUp}>
                {merged.signUpSubmit}
              </Button>
            </>
          ) : (
            <>
              <FormField label={merged.emailLabel} required error={errors?.email}>
                <Input
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={(e) => onChange('email', e.target.value)}
                  error={Boolean(errors?.email)}
                  aria-label={merged.emailLabel}
                />
              </FormField>

              <FormField label={merged.passwordLabel} required error={errors?.password}>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={values.password}
                  onChange={(e) => onChange('password', e.target.value)}
                  error={Boolean(errors?.password)}
                  aria-label={merged.passwordLabel}
                />
              </FormField>

              {errors?.form && <FormError role="alert">{errors.form}</FormError>}

              <Button type="submit" variant="primary" fullWidth disabled={isSubmitting}>
                {merged.submit}
              </Button>
            </>
          )}
        </FormBlock>

        {!isSignUp && onForgottenPasswordClick && (
          <ForgotRow>
            <InlineLink type="button" onClick={onForgottenPasswordClick}>
              {merged.forgottenPassword}
            </InlineLink>
          </ForgotRow>
        )}
      </Panel>
    </Modal>
  );
}
